/**
 * Calendar-date primitives for YYYY-MM-DD strings — the form every domain date is stored and
 * carried in (see `@backend/schemas/calendarDate` for why a string rather than a `Date`).
 *
 * The module deliberately imports nothing, so it is safe both on the forecast engine's hot path
 * and inside the pure capital-model helpers `src/` pulls in through the `@backend` alias.
 */

/** A calendar date split into its numeric parts. */
export interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * The number of days in a calendar month.
 *
 * @param year - The four-digit year
 * @param month - The 1-based month
 * @returns The month's last day
 */
const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Render date parts as YYYY-MM-DD, so two dates compare as plain strings.
 *
 * @param parts - The date parts
 * @returns The calendar date string
 */
export const formatCalendarDate = (parts: CalendarDateParts): string =>
  `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;

/**
 * Split a YYYY-MM-DD string into its parts, rejecting anything that is not a real calendar date.
 * Validity is a `Date.UTC` round trip rather than a range check, so `2026-02-31` — which a
 * `1..31` day range would admit — is rejected along with `2026-13-01`. A side effect of that round
 * trip: `Date.UTC` maps a two-digit year onto 19xx, so a year under 100 is rejected too. No domain
 * date predates the year 1000, and this matches what `calendarDateSchema` has always accepted.
 *
 * @param value - The candidate date string
 * @returns The parts, or null when the string is not a well-formed, real calendar date
 */
export const parseCalendarDate = (value: string): CalendarDateParts | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match)
    return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const roundTrip = new Date(Date.UTC(year, month - 1, day));
  const isReal = roundTrip.getUTCFullYear() === year
    && roundTrip.getUTCMonth() === month - 1
    && roundTrip.getUTCDate() === day;

  if (!isReal)
    return null;

  return { year, month, day };
};

/**
 * Whether a string is a real YYYY-MM-DD calendar date.
 *
 * @param value - The candidate date string
 * @returns true when the string names a date that exists
 */
export const isValidCalendarDate = (value: string): boolean => parseCalendarDate(value) !== null;

/**
 * Add a whole number of months to a YYYY-MM-DD date, clamping the day to the target month's
 * length (e.g. Jan 31 + 1mo → Feb 28).
 *
 * @param dateStr - A date in YYYY-MM-DD
 * @param months - Whole months to add; may be zero or negative
 * @returns The shifted date in YYYY-MM-DD
 */
export const addMonths = (dateStr: string, months: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const base = new Date(Date.UTC(y!, m! - 1 + months, 1));
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth() + 1;

  return formatCalendarDate({ year, month, day: Math.min(d!, daysInMonth(year, month)) });
};

/**
 * Add a whole number of days to a YYYY-MM-DD date.
 *
 * @param dateStr - A date in YYYY-MM-DD
 * @param days - Whole days to add; may be zero or negative
 * @returns The shifted date in YYYY-MM-DD
 */
export const addDays = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const shifted = new Date(Date.UTC(y!, m! - 1, d! + days));

  return formatCalendarDate({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  });
};
